/**
 * The DATA a document form can place — read off the real transaction models.
 *
 * One catalog, two consumers: the Body tab's placement panel (UX-A1) and the
 * `@` reference picker (UX-A3). They must offer the same things by the same
 * names, or an author would learn two vocabularies for one model — so the
 * catalog is built once, here, and neither consumer derives anything of its
 * own from a raw model.
 *
 * **Nothing here is inferred from a name.** A hop exists because a column
 * declares `lookup` or `must_exist_in`; a form is a detail form because its
 * `form_no` is not 1. Where the model does not say, this module returns
 * nothing rather than a guess — the same rule `er/bindings.py` holds to on the
 * other side, and for the same reason: a binding nobody authored is a binding
 * nobody can be asked about.
 *
 * Pure. No React, no network — `test_er_sources.mjs` runs it on fixtures.
 */

/** One column of one form, flattened out of the transaction model. */
export interface SourceColumn {
  /** The transaction's own form key — lower-case (`invoice_no`). */
  name: string
  /** The database column — UPPER (`INVOICE_NO`). What a path segment is. */
  dbName: string
  /** What a person calls it. The friendly half of a reference label. */
  heading: string
  colType: string
  key: boolean
  mandatory: boolean
  /** The transaction protects it, or allocates it — never an entry box. */
  readOnly: boolean
  /** Why it is read-only, for the log line. Empty when it is not. */
  readOnlyReason: string
  hidden: boolean
  /** The object this column reaches, when it declares one. */
  hop: { object: string; displayColumn: string; valueColumn: string } | null
  /**
   * The GROUP its own model files it under — a visual's `ColumnGroup`.
   *
   * A visual is laid out in groups on purpose ("Customer", "Ageing",
   * "Amounts"), and flattening them into one alphabetical run throws away the
   * only arrangement the author has already made. Empty for a transaction
   * column, whose form is its grouping.
   */
  group?: string
  /**
   * A STANDARD additional attribute (`uda_<key>`) the model's design layer
   * (`transaction.uda`) declares on this form. It is a field of the record
   * like any other — its value is read and written through the transaction —
   * but it APPLIES only to records whose deciding values call for it, so it
   * is never `mandatory` here. An organization's own attributes live in its
   * database, not in the model, and are not listed.
   */
  attribute?: boolean
}

/** One form of one transaction. */
export interface SourceForm {
  formNo: string
  title: string
  table: string
  /** `false` for form 1. A detail form is what a repeat loops over. */
  detail: boolean
  /**
   * This form is a MANY source's header — a set that is still the top level.
   *
   * `er/execution.py:_is_set_header`, on this side of the wire. A visual's
   * result is `detail` (a reference to it outside a repeat is every row) and
   * is at the same time the level a heading reads from: a merge field over it
   * resolves to the FIRST row, which on a document covering one identity is
   * that identity's own row. Both facts are true, and telling them apart is
   * what lets the panel place a customer's name without first asking which of
   * the many names is meant.
   */
  setHeader?: boolean
  columns: SourceColumn[]
}

/** One source the document covers, as the panel groups it. */
export interface SourceTxn {
  /** The PATH segment — the alias when there is one, else the obj_name. */
  ident: string
  objName: string
  title: string
  role: 'primary' | 'linked'
  /**
   * A transaction (records) or a VISUAL (a computed result set).
   *
   * Absent means transaction, which is what every form written before visual
   * sources existed means.
   */
  kind: 'transaction' | 'visual'
  readOnly: boolean
  forms: SourceForm[]
  /**
   * A visual's own prompts — what the run is filtered by, and what a `prompt`
   * key of the document maps onto. Empty for a transaction.
   */
  prompts: { name: string; label: string; type: string }[]
}

const AUTO_GENERATE = /^\s*auto_generate(_prefixed|_device)?\s*\(/i

/** `CRM_LEAD(LEAD_NAME, LEAD_NO)` → the object and its two columns. */
export function parseLookup(raw: unknown): SourceColumn['hop'] {
  const text = String(raw || '').trim()
  if (!text) return null
  const match = /^([A-Za-z_][A-Za-z0-9_]*)\s*\(([^)]*)\)\s*$/.exec(text)
  if (!match) {
    // `must_exist_in` spelling: the object alone, with no columns named.
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(text)) {
      return { object: text.toUpperCase(), displayColumn: '', valueColumn: '' }
    }
    return null
  }
  const parts = match[2].split(',').map((p) => p.trim()).filter(Boolean)
  return {
    object: match[1].toUpperCase(),
    displayColumn: (parts[0] || '').toUpperCase(),
    // A lookup names (what is shown, what is stored). The stored half is what
    // a match is made on; when only one is given it is both.
    valueColumn: (parts[1] || parts[0] || '').toUpperCase(),
  }
}

function truthy(v: unknown): boolean {
  if (v === true) return true
  if (typeof v === 'number') return v !== 0
  const t = String(v ?? '').trim().toLowerCase()
  return t !== '' && t !== '0' && t !== 'false' && t !== 'n' && t !== 'no'
}

function columnOf(raw: Record<string, unknown>): SourceColumn | null {
  const col = (raw?.column || raw) as Record<string, unknown>
  const name = String(col?.name || '').trim()
  if (!name) return null
  const dbName = String(col?.db_name || name).trim().toUpperCase()
  const defaultValue = String(col?.default_value || '')
  const allocated = AUTO_GENERATE.test(defaultValue)
  const protect = truthy(col?.protect)
  const expression = String(col?.expression || '').trim() !== ''

  let reason = ''
  if (allocated) reason = 'the transaction allocates it'
  else if (expression) reason = 'the transaction computes it'
  else if (protect) reason = 'the transaction protects it'

  return {
    name,
    dbName,
    heading: String(col?.heading || col?.descr || name).trim(),
    colType: String(col?.col_type || 'VARCHAR').trim().toUpperCase(),
    key: truthy(col?.key),
    mandatory: truthy(col?.mandatory),
    readOnly: reason !== '',
    readOnlyReason: reason,
    hidden: truthy(col?.hidden),
    hop: parseLookup(col?.lookup) || parseLookup(col?.must_exist_in),
  }
}

const ATTRIBUTE_KEY = /^[a-z][a-z0-9_]*$/

/**
 * The standard additional attributes of one form, from the model's design
 * layer — the engine's `effective.inject` filters: an enabled form, visible
 * sets, valid names, `retired` left out, the first set naming a column wins.
 */
function attributeColumnsOf(txn: Record<string, unknown> | undefined,
                            formNo: string, taken: Set<string>): SourceColumn[] {
  const layer = txn?.uda as { forms?: Record<string, Record<string, unknown>> } | undefined
  const form = layer?.forms?.[formNo]
  if (!form || form.enabled === false) return []
  const out: SourceColumn[] = []
  for (const set of (Array.isArray(form.sets) ? form.sets : []) as Record<string, unknown>[]) {
    if (!set || set.hidden) continue
    for (const col of (Array.isArray(set.columns) ? set.columns : []) as
      Record<string, unknown>[]) {
      const key = String(col?.name || '').trim()
      if (!key || col.retired || !ATTRIBUTE_KEY.test(key)) continue
      const name = `uda_${key}`
      if (taken.has(name)) continue
      taken.add(name)
      const parsed = columnOf({ ...col, name, db_name: name.toUpperCase(),
                                key: false, mandatory: false })
      if (parsed && !parsed.hidden) out.push({ ...parsed, attribute: true })
    }
  }
  return out
}

/**
 * One transaction model → its forms and columns.
 *
 * The model nests a form as `sql_models[].sql_model`, which is the shape on
 * disk; reading it anywhere else in this designer would be a second place that
 * has to change when it moves.
 */
export function formsOfModel(model: unknown): SourceForm[] {
  // Tolerant of the transport envelope. `/designers/load-transaction-object`
  // answers `{success, content: {transaction: …}}`, and reading `.transaction`
  // off the envelope silently yields NO forms — a panel that renders empty
  // over a perfectly good transaction, with nothing to search and no error
  // anywhere. Unwrapping here means every consumer is right, not just the one
  // that remembered.
  const given = model as Record<string, unknown> | undefined
  const root = (given && !given.transaction && given.content
    ? given.content : given) as Record<string, unknown> | undefined
  const txn = root?.transaction as Record<string, unknown> | undefined
  const models = Array.isArray(txn?.sql_models) ? txn.sql_models : []
  const out: SourceForm[] = []
  for (const entry of models as Record<string, unknown>[]) {
    const form = (entry?.sql_model || entry) as Record<string, unknown>
    if (!form) continue
    const formNo = String(form.form_no ?? '1').trim() || '1'
    const columns: SourceColumn[] = []
    for (const c of (Array.isArray(form.columns) ? form.columns : []) as
      Record<string, unknown>[]) {
      const parsed = columnOf(c)
      if (parsed && !parsed.hidden) columns.push(parsed)
    }
    // The form's standard additional attributes, after its own columns.
    columns.push(...attributeColumnsOf(txn, formNo, new Set(columns.map((c) => c.name))))
    out.push({
      formNo,
      title: String(form.form_title || form.form_descr || `Form ${formNo}`).trim(),
      table: String(form.main_table || '').trim().toUpperCase(),
      detail: formNo !== '1',
      columns,
    })
  }
  return out
}

/**
 * One VISUAL model → the single form a document sees.
 *
 * A visual has no header/detail split: it has a result, and the document
 * addresses it as form "1" exactly as it addresses a transaction's rows. So
 * this returns ONE form, marked `detail` — because what a document does with a
 * set of rows is loop over it, and `detail` is what makes the panel offer to.
 *
 * Every column comes back READ-ONLY, and not as a setting somebody could turn
 * off: a visual column is worked out from other data — a SUM, a join, an
 * expression — and there is no record behind it to write a typed value to. The
 * panel therefore offers them as merge fields and aggregates only, through the
 * same `readOnly` rule that already stops somebody typing into an
 * auto-generated invoice number.
 */
export function formsOfVisual(model: unknown): SourceForm[] {
  const given = model as Record<string, unknown> | undefined
  const root = (given && !given.SQLMODEL && given.content
    ? given.content : given) as Record<string, unknown> | undefined
  if (!root) return []
  const sqlmodel = (root.SQLMODEL || {}) as Record<string, unknown>
  const columns: SourceColumn[] = []
  const seen = new Set<string>()

  const push = (
    name: string, heading: string, colType: string, group: string,
  ) => {
    const dbName = String(name || '').trim().toUpperCase()
    if (!dbName || seen.has(dbName)) return
    seen.add(dbName)
    columns.push({
      // A visual row is keyed by the SELECT alias, and the engine upper-cases
      // it once at the boundary (`read_through_visual`). Both names are that
      // one spelling here, so nothing downstream re-cases it.
      name: dbName,
      dbName,
      heading: heading || prettyColumn(dbName),
      colType: colType || '',
      key: false,
      mandatory: false,
      readOnly: true,
      readOnlyReason: 'the visual works it out',
      hidden: false,
      hop: null,
      // The author's OWN arrangement, kept. The panel groups by it rather
      // than showing one run of forty columns in whatever order the SELECT
      // happened to put them in.
      group: String(group || '').trim(),
    })
  }

  // Schema mode: the master column list IS the SELECT.
  const master = ((sqlmodel.SQLModel as Record<string, unknown> | undefined)
    ?.COLUMNS || []) as Record<string, unknown>[]
  for (const group of master) {
    const groupName = groupNameOf(group)
    for (const col of (Array.isArray(group?.COLUMN) ? group.COLUMN : []) as
      Record<string, unknown>[]) {
      if (col?.HIDDEN) continue
      push(String(col?.NAME || col?.DBNAME || ''),
           String(col?.content || col?.heading || ''),
           String(col?.COLTYPE || ''), groupName)
    }
  }

  // SQL mode: the aliases of the author's own SELECT. Read from the LAYOUT
  // groups, which is where a placed column lands in either mode — so a visual
  // the author has laid out offers exactly the columns they placed.
  const layout = ((root.LAYOUT_DATA || {}) as Record<string, unknown>)
    .visualLayout as Record<string, unknown> | undefined
  for (const group of (Array.isArray(layout?.ColumnGroups)
    ? layout.ColumnGroups : []) as Record<string, unknown>[]) {
    const groupName = groupNameOf(group)
    for (const col of (Array.isArray(group?.COLUMNS) ? group.COLUMNS : []) as
      Record<string, unknown>[]) {
      if (col?.HIDDEN) continue
      push(String(col?.NAME || col?.DBNAME || ''),
           String(col?.content || col?.heading || ''),
           String(col?.COLTYPE || ''), groupName)
    }
  }

  return [{
    formNo: '1',
    title: 'Result',
    table: String(sqlmodel.SCHEMA_NAME || '').trim().toUpperCase(),
    // A visual's rows are a SET, so the panel treats them the way it treats a
    // detail form: place the table, loop the rows, total them in the footer.
    detail: true,
    // …and at the same time the level a HEADING reads from — see `setHeader`.
    setHeader: true,
    columns,
  }]
}

/**
 * What a visual calls one of its column groups. `''` when it names none.
 *
 * `GroupName` is the layout's own word for it — "Category", "Value", the
 * author's own heading on a tabular visual. The master `SQLModel.COLUMNS`
 * entries carry no name at all, and answering `''` for those is what keeps
 * them in one unlabelled run instead of inventing a heading for them.
 */
function groupNameOf(group: Record<string, unknown> | undefined): string {
  return String(group?.GroupName || group?.GroupDescription || '').trim()
}

/**
 * A visual's run-time prompts — its criteria, by the names the engine binds.
 *
 * Read from the same two places `extract_prompt_vars` reads on the server: a
 * criteria rule marked as a prompt, and a `?.name` placeholder in freehand
 * SQL. Kept simple deliberately — this feeds a PICKER, and a prompt it misses
 * can still be typed; a prompt it invents would be a criterion nobody can
 * answer.
 */
export function promptsOfVisual(model: unknown): SourceTxn['prompts'] {
  const given = model as Record<string, unknown> | undefined
  const root = (given && !given.SQLMODEL && given.content
    ? given.content : given) as Record<string, unknown> | undefined
  if (!root) return []
  const out: SourceTxn['prompts'] = []
  const seen = new Set<string>()
  const add = (name: string, label: string, type: string) => {
    const key = String(name || '').trim().toUpperCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push({ name: key, label: label || prettyColumn(key), type: type || '' })
  }

  const sqlmodel = (root.SQLMODEL || {}) as Record<string, unknown>
  const criteria = (sqlmodel.CRITERIA || {}) as Record<string, unknown>
  const query = (criteria.query || {}) as Record<string, unknown>
  const walk = (node: Record<string, unknown> | undefined) => {
    if (!node) return
    for (const rule of (Array.isArray(node.rules) ? node.rules : []) as
      Record<string, unknown>[]) {
      if (Array.isArray(rule?.rules)) { walk(rule); continue }
      if (String(rule?.queryOption || '') !== 'P') continue
      add(String(rule?.field || ''), String(rule?.promptLabel || ''),
          String(rule?.COLTYPE || rule?.currentFieldType || ''))
    }
  }
  walk(query)

  // Freehand SQL: `?.name`, optionally `?.name.type.default`.
  const sql = String(root.SOURCE_SQL || '')
  for (const match of sql.matchAll(/\?\.([A-Za-z_][A-Za-z0-9_]*)/g)) {
    add(match[1], '', '')
  }
  return out
}

function prettyColumn(name: string): string {
  return name.replace(/_/g, ' ').toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
}

/** Every object reachable in one hop from a transaction, with the column. */
export function hopsOf(forms: SourceForm[]): {
  object: string; viaForm: string; viaColumn: string
  displayColumn: string; valueColumn: string
}[] {
  const seen = new Set<string>()
  const out: ReturnType<typeof hopsOf> = []
  for (const form of forms) {
    for (const col of form.columns) {
      if (!col.hop?.object) continue
      const id = `${col.hop.object}|${form.formNo}|${col.dbName}`
      if (seen.has(id)) continue
      seen.add(id)
      out.push({
        object: col.hop.object,
        viaForm: form.formNo,
        viaColumn: col.dbName,
        displayColumn: col.hop.displayColumn,
        valueColumn: col.hop.valueColumn,
      })
    }
  }
  return out
}

/**
 * The catalog the panel draws, assembled from the form's `transactions[]` and
 * the models already fetched for them.
 *
 * A transaction whose model has not arrived yet appears with no forms rather
 * than being left out: the group is the place the author would look for it,
 * and a panel that silently omits a declared transaction reads as a form that
 * does not cover it.
 */
export function buildCatalog(
  txns: {
    obj_name: string; alias?: string; role?: string; read_only?: boolean
    kind?: string
  }[],
  models: Record<string, unknown>,
): SourceTxn[] {
  return (txns || []).filter((t) => t?.obj_name).map((t) => {
    const objName = String(t.obj_name).trim()
    const ident = String(t.alias || objName).trim().toUpperCase()
    const kind = t.kind === 'visual' ? 'visual' : 'transaction'
    // Keyed by kind as well as name — a Document visual and its document form
    // share a name by design, so a name-only key returns the wrong model and
    // the reader run over it produces an empty column list. The bare-name
    // fallbacks are kept for a caller that primed the cache the old way.
    const model = models[`${kind === 'visual' ? 'V' : 'T'}:${objName.toUpperCase()}`]
      ?? models[objName.toUpperCase()] ?? models[objName]
    return {
      ident,
      objName: objName.toUpperCase(),
      title: objName.replace(/_/g, ' '),
      role: t.role === 'primary' ? 'primary' : 'linked',
      kind,
      // A visual is read-only by what it IS, not by a setting — so the flag is
      // true here whatever the model says, and the panel's existing rule
      // (read-only ⇒ merge fields only) needs no second case.
      readOnly: kind === 'visual' ? true : !!t.read_only,
      forms: model
        ? (kind === 'visual' ? formsOfVisual(model) : formsOfModel(model))
        : [],
      prompts: model && kind === 'visual' ? promptsOfVisual(model) : [],
    }
  })
}
